import { UseFormSetError } from "react-hook-form";

/**
 * Validation 422s carry a `fields` map next to `errors`, keyed by the request
 * field path exactly as we sent it (`storeQuantities.0.barcodes.2`). Without it
 * a rejected save is one toast that never says which input was wrong, so any
 * form in the dashboard can call this and let the message land on the field.
 *
 * Non-validation 422s have no `fields` — they carry `errorKey` instead — and
 * this returns false for them so the caller can fall back to its own handling.
 */
export const applyServerFieldErrors = (
  error: any,
  setError: UseFormSetError<any>,
): boolean => {
  const fields = error?.response?.data?.fields;

  if (!fields || typeof fields !== "object") return false;

  let applied = false;

  Object.keys(fields).forEach((path) => {
    const value = fields[path];
    const message = Array.isArray(value) ? value[0] : value;

    if (typeof message !== "string" || !message) return;

    setError(path as any, { type: "server", message });
    applied = true;
  });

  return applied;
};

/**
 * Collects every message stored at or under a field path. A server error on
 * `storeQuantities.0.barcodes.2` sits below the input that renders the whole
 * `barcodes` array, so the input has to look down to find it.
 */
export const getFieldErrorMessages = (errors: any, path: string): string[] => {
  const node = path
    .split(".")
    .reduce((current: any, key: string) => current?.[key], errors);

  if (!node) return [];

  const messages: string[] = [];

  const walk = (value: any) => {
    if (!value || typeof value !== "object") return;

    if (typeof value.message === "string" && value.message) {
      messages.push(value.message);
    }

    Object.keys(value).forEach((key) => {
      if (key === "message" || key === "type" || key === "ref") return;
      walk(value[key]);
    });
  };

  walk(node);

  return Array.from(new Set(messages));
};
